import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  CHALLENGE_TEXTS, ChallengeBoard, ChallengeBoardKind, ChallengeBoardRow, ChallengeMe, ChallengeRide, ChallengeRideDraft, ChallengeSummary,
  challengeDisplayNameFor, challengeTeamIntent, challengeToday, teamInviteNextStep, TEAM_REQUEST_TEXTS,
} from '@radevents/shared';
import PublicHeader from '../components/Layout/PublicHeader';
import WinterchallengeView from '../components/winterchallenge/WinterchallengeView';
import { JoinDraft } from '../components/winterchallenge/ChallengeParts';
import { messageOf, statusOf } from '../components/winterchallenge/messageOf';
import { Spinner } from '../components/ui';
import { useAuth } from '../contexts/AuthContext';
import { SITE_ORIGIN } from '../config/siteOrigin';
import { forgetChallengeMe } from '../components/WinterchallengePromoBanner';
import challengeService from '../services/challengeService';

const KINDS: ChallengeBoardKind[] = ['riders', 'teams', 'regions', 'countries'];
const CANONICAL = `${SITE_ORIGIN}/winterchallenge`;
const OG_IMAGE = `${SITE_ORIGIN}/bilder/winterchallenge/fahrer.webp`;
const DESC = 'Kilometer sammeln über den Winter, allein oder im Team. Mit Wertungen für Fahrer, Teams, Bundesländer und Länder.';

/**
 * Der Wunsch mitzumachen überlebt die Anmeldung im sessionStorage, falls ein Weg die
 * Rücksprungadresse verliert (Registrierung, Bestätigungslink). Er gilt 30 Minuten und wird beim
 * nächsten Besuch der Seite gelesen und gelöscht, damit er nicht Tage später einen Dialog öffnet.
 */
const JOIN_INTENT = 'wc-mitmachen';
const JOIN_INTENT_MS = 30 * 60 * 1000;

export function rememberJoinIntent() {
  sessionStorage.setItem(JOIN_INTENT, String(Date.now()));
}

/** Liest den gemerkten Wunsch einmal und räumt ihn weg. */
function takeJoinIntent(): boolean {
  const raw = sessionStorage.getItem(JOIN_INTENT);
  sessionStorage.removeItem(JOIN_INTENT);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < JOIN_INTENT_MS;
}

/**
 * `/winterchallenge` (`docs/bereiche/winterchallenge.md`). Lädt und speichert, gezeichnet wird in
 * `WinterchallengeView`. Nicht angemeldet führt „Mitmachen“ über die Anmeldung zurück hierher und
 * öffnet dann gleich das Formular (`?mitmachen=1`).
 */
const Winterchallenge: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading, patchUser, detailsPromptOpen } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Von einer Teamseite gekommen: mit Einladungslink (`code`) oder um anzufragen (`anfrage=1`).
  const teamIntent = challengeTeamIntent(params.get('team'), params.get('code'), params.get('anfrage'));
  const teamReturn = teamIntent?.returnPath ?? null;
  const [summary, setSummary] = useState<ChallengeSummary | null>(null);
  /** `noSeason`: der Server kennt gerade keine Saison (404); `failed`: nicht erreichbar. */
  const [unavailable, setUnavailable] = useState<'noSeason' | 'failed' | null>(null);
  const [me, setMe] = useState<ChallengeMe | null>(null);
  /** Erst wenn feststeht, ob das Konto schon mitmacht, darf sich „Mitmachen“ von selbst öffnen. */
  const [meLoaded, setMeLoaded] = useState(false);
  const [boards, setBoards] = useState<Partial<Record<ChallengeBoardKind, ChallengeBoard>>>({});
  const [boardsFailed, setBoardsFailed] = useState<Partial<Record<ChallengeBoardKind, boolean>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadBoards = useCallback(() => {
    KINDS.forEach(kind => {
      challengeService.board(kind)
        .then(b => { setBoards(prev => ({ ...prev, [kind]: b })); setBoardsFailed(prev => ({ ...prev, [kind]: false })); })
        .catch(() => setBoardsFailed(prev => ({ ...prev, [kind]: true })));
    });
  }, []);

  const refresh = useCallback(() => {
    challengeService.summary()
      .then(s => { setSummary(s); setUnavailable(null); })
      .catch(err => setUnavailable(statusOf(err) === 404 ? 'noSeason' : 'failed'));
    loadBoards();
  }, [loadBoards]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!isAuthenticated) { setMe(null); setMeLoaded(false); return; }
    challengeService.me().then(setMe).catch(() => setMe(null)).finally(() => setMeLoaded(true));
  }, [isAuthenticated]);

  // Der Wunsch mitzumachen: aus der Rücksprungadresse (`?mitmachen=1`) oder dem Speicher, einmal
  // beim Öffnen gelesen. Der Dialog wartet, bis ein neues Konto die Frage nach Geburtsjahr und
  // Radarten hinter sich hat.
  const [storedIntent] = useState(takeJoinIntent);
  const intent = params.get('mitmachen') === '1' || storedIntent;
  const [autoJoin, setAutoJoin] = useState(false);
  useEffect(() => {
    if (!intent || !isAuthenticated || !meLoaded || detailsPromptOpen) return;
    if (!me) setAutoJoin(true);
    if (params.has('mitmachen')) { params.delete('mitmachen'); setParams(params, { replace: true }); }
  }, [intent, isAuthenticated, meLoaded, detailsPromptOpen, me, params, setParams]);

  // Aus dem Profil („Kilometer eintragen“, `?eintragen=1`): das Formular steht gleich offen,
  // sobald feststeht, dass das Konto mitmacht.
  const ridePanel = params.get('eintragen') === '1' && !!me;
  const initialPanel = autoJoin ? 'join' : ridePanel ? 'ride' : null;

  // Über einen Einladungslink gekommen: der Klick auf „Beitreten“ gilt. Wer jetzt mitmacht, ist
  // gleich im Team, ohne auf der Teamseite ein zweites Mal beizutreten, und bleibt hier: auf
  // dieser Seite steht, wie es geht, und „Kilometer eintragen“. Die Teamseite allein erklärt
  // einem Neuen nichts (Phil, 25.10.2026).
  const teamSlug = teamIntent?.slug ?? null;
  const [teamName, setTeamName] = useState<string | null>(null);
  useEffect(() => {
    if (teamSlug) challengeService.team(teamSlug).then(t => setTeamName(t.name)).catch(() => setTeamName(null));
  }, [teamSlug]);
  const teamHandled = useRef(false);
  useEffect(() => {
    if (!teamReturn || !teamSlug || !meLoaded || !me || detailsPromptOpen || teamHandled.current) return;
    teamHandled.current = true;
    const step = teamInviteNextStep(me, teamSlug);
    if (step === 'otherTeam') { navigate(teamReturn, { replace: true }); return; }
    const stay = () => navigate('/winterchallenge', { replace: true });
    if (step === 'already') { stay(); return; }
    const viaRequest = !teamIntent?.code;
    (viaRequest ? challengeService.requestTeam(teamSlug) : challengeService.joinTeam(teamSlug, teamIntent?.code ?? ''))
      .then(team => {
        forgetChallengeMe();
        toast.success(viaRequest ? TEAM_REQUEST_TEXTS.sentAfterJoin(team.name) : `Du bist dabei und fährst für ${team.name}`);
        challengeService.me().then(setMe).catch(() => {});
        stay();
      })
      .catch(err => {
        toast.error(messageOf(err, viaRequest ? 'Die Anfrage ist nicht angekommen.' : 'Dem Team beizutreten hat nicht geklappt.'));
        navigate(teamReturn, { replace: true });
      });
  }, [teamReturn, teamSlug, teamIntent?.code, meLoaded, me, detailsPromptOpen, navigate]);

  const afterChange = (next: ChallengeMe) => {
    setMe(next);
    forgetChallengeMe();
    setError(null);
    refresh();
  };

  const join = async (draft: JoinDraft) => {
    setSaving(true);
    try {
      const next = await challengeService.join(draft);
      patchUser({ bundesland: next.bundesland ?? draft.bundesland });
      afterChange(next);
      // Mit Einladungslink kommt die Meldung erst nach dem Teambeitritt, sonst stünden zwei da.
      if (!teamReturn) toast.success('Du bist dabei');
    } catch (err) {
      setError(messageOf(err, 'Das Mitmachen hat nicht geklappt. Versuch es noch einmal.'));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const saveRide = async (draft: ChallengeRideDraft, rideId: number | null) => {
    setSaving(true);
    try {
      const next = rideId == null ? await challengeService.addRide(draft) : await challengeService.updateRide(rideId, draft);
      afterChange(next);
      toast.success(rideId == null ? 'Fahrt eingetragen' : 'Fahrt gespeichert');
    } catch (err) {
      setError(messageOf(err, 'Die Fahrt wurde nicht gespeichert.'));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const rename = async (displayName: string) => {
    setSaving(true);
    try {
      afterChange(await challengeService.rename(displayName));
      toast.success('Anzeigename gespeichert');
    } catch (err) {
      setError(messageOf(err, 'Der Anzeigename wurde nicht gespeichert.'));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const deleteRide = async (ride: ChallengeRide) => {
    if (!window.confirm('Fahrt löschen?')) return;
    try {
      afterChange(await challengeService.deleteRide(ride.id));
      toast.success('Fahrt gelöscht');
    } catch (err) {
      toast.error(messageOf(err, 'Die Fahrt wurde nicht gelöscht.'));
    }
  };

  const report = async (row: ChallengeBoardRow) => {
    if (row.id == null) return;
    if (!window.confirm(`${row.name} melden? Die Redaktion schaut sich die Fahrten an.`)) return;
    try {
      await challengeService.report(row.id);
      toast.success('Danke, wir schauen es uns an');
    } catch (err) {
      toast.error(messageOf(err, 'Die Meldung ist nicht angekommen.'));
    }
  };

  const login = () => {
    rememberJoinIntent();
    const back = '/winterchallenge?mitmachen=1' + (teamIntent?.query ?? '');
    navigate(`/login?redirect=${encodeURIComponent(back)}`);
  };

  return (
    <div className="has-bottom-nav" style={{ minHeight: '100vh', background: '#F5F3F3', display: 'flex', flexDirection: 'column' }}>
      <Helmet>
        <title>{summary ? `${summary.title} | Radsport Events` : 'Winterchallenge | Radsport Events'}</title>
        <meta name="description" content={DESC} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:title" content={summary?.title ?? 'Winterchallenge'} />
        <meta property="og:description" content={DESC} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:type" content="website" />
      </Helmet>
      <PublicHeader />

      <main style={{ flex: 1 }}>
        {/* Erst zeichnen, wenn feststeht, ob das Konto mitmacht: sonst steht kurz der Gast-Kopf mit
            dem Bild da und springt dann auf den schmalen Kopf für Teilnehmer. */}
        {summary && !authLoading && (!isAuthenticated || meLoaded) ? (
          <WinterchallengeView
            summary={summary}
            today={challengeToday()}
            me={me}
            boards={boards}
            boardsUnavailable={boardsFailed}
            loggedIn={isAuthenticated}
            joinInitial={{ displayName: challengeDisplayNameFor(user), bundesland: user?.bundesland ?? '' }}
            joinTeamName={teamName}
            joinTeamRequest={!!teamIntent && !teamIntent.code}
            initialPanel={initialPanel}
            key={initialPanel ?? 'page'}
            saving={saving}
            error={error}
            onClearError={() => setError(null)}
            onLogin={login}
            onJoin={join}
            onSaveRide={saveRide}
            onDeleteRide={deleteRide}
            onRename={rename}
            onReport={report}
            onShowAll={kind => navigate(`/winterchallenge/rangliste/${kind}`)}
            onCreateTeam={() => navigate('/winterchallenge/team-gruenden')}
          />
        ) : unavailable ? (
          <p className="wc-empty" style={{ maxWidth: 560, margin: '60px auto' }} role={unavailable === 'failed' ? 'alert' : undefined}>
            {unavailable === 'noSeason' ? CHALLENGE_TEXTS.noSeason : CHALLENGE_TEXTS.pageUnavailable}
          </p>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}><Spinner /></div>
        )}
      </main>

    </div>
  );
};

export default Winterchallenge;
